"use client";

import { useState, useEffect, useRef } from "react";
import { storyblokEditable, StoryblokComponent } from "@storyblok/react/rsc";
import Link from "next/link";
import { useAuth } from "@clerk/nextjs";

import { Button } from "@/components/ui/button";
import { GridContainer } from "@/components/layout/GridContainer";
import { ArrowRight } from "@/components/icons/ArrowRight";
import { SignupModal } from "@/components/layout/SignupModal";
import { AuthCard } from "@/components/auth/AuthCard";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import type { MultilinkStoryblok } from "@/component-types-sb";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/analytics";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";

type PricingOption = {
  _uid: string;
  label: string;
  price: number;
  time_period: "monthly" | "yearly";
  badge?: string;
};

const periodLabels: Record<string, string> = {
  monthly: "pro Monat",
  yearly: "pro Jahr",
};

type PricingSectionStoryblok = {
  headline?: string;
  features?: any[];
  options?: PricingOption[];
  button_label?: string;
  button_link?: MultilinkStoryblok;
  button_type?: string;
  disclaimer_list?: any[];
  _uid: string;
  component: "pricing_section";
  [k: string]: any;
};

type PricingSectionProps = {
  blok: PricingSectionStoryblok;
};

type LivePricing = {
  monthly: { amount: number; currency: string; originalPrice: number | null };
  yearly: { amount: number; currency: string; originalPrice: number | null };
  isLaunchPricing: boolean;
} | null;

function formatEur(cents: number): string {
  return `${(cents / 100).toFixed(0).replace(".", ",")} €`;
}

const PricingSection = ({ blok }: PricingSectionProps) => {
  const options = blok.options || [];
  const [selectedIndex, setSelectedIndex] = useState(
    options.length > 1 ? 1 : 0,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [livePricing, setLivePricing] = useState<LivePricing>(null);
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  /** Trennt "Auth-Modal erfolgreich" von "Auth-Modal abgebrochen". */
  const authCompleted = useRef(false);

  const checkoutEnabled = process.env.NEXT_PUBLIC_CHECKOUT_ENABLED === "true";
  const buttonType = blok.button_type || (options.length > 0 ? "checkout" : "link");
  const isCheckout = buttonType === "checkout" || buttonType === "membership_payment";
  const signupTopic = !isCheckout && buttonType !== "link" ? buttonType : undefined;

  type ResumeData = {
    utm_source?: string | null;
    utm_campaign?: string | null;
    utm_content?: string | null;
    utm_medium?: string | null;
  };
  const [resumeData, setResumeData] = useState<ResumeData | null>(null);
  const [pendingCheckout, setPendingCheckout] = useState(false);

  useEffect(() => {
    if (!isCheckout) return;
    try {
      const raw = sessionStorage.getItem("checkout_resume");
      sessionStorage.removeItem("checkout_resume");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const MAX_AGE = 10 * 60 * 1000;
      if (parsed.path === window.location.pathname && Date.now() - parsed.ts < MAX_AGE) {
        setResumeData({
          utm_source: parsed.utm_source,
          utm_campaign: parsed.utm_campaign,
          utm_content: parsed.utm_content,
          utm_medium: parsed.utm_medium,
        });
      }
    } catch {}
  }, [isCheckout]);

  useEffect(() => {
    if (!isCheckout) return;
    fetch("/api/pricing")
      .then((r) => r.json())
      .then((data) => {
        console.group("[PricingSection] /api/pricing response");
        console.log("Raw API data:", JSON.stringify(data, null, 2));
        console.log("isLaunchPricing:", data.isLaunchPricing);
        console.log("Monthly — amount:", data.monthly?.amount, "cents =", (data.monthly?.amount / 100) + "€", "| originalPrice:", data.monthly?.originalPrice, "cents =", data.monthly?.originalPrice ? (data.monthly.originalPrice / 100) + "€" : "null");
        console.log("Yearly — amount:", data.yearly?.amount, "cents =", (data.yearly?.amount / 100) + "€", "| originalPrice:", data.yearly?.originalPrice, "cents =", data.yearly?.originalPrice ? (data.yearly.originalPrice / 100) + "€" : "null");
        console.groupEnd();
        setLivePricing(data);
      })
      .catch((err) => console.error("[PricingSection] /api/pricing fetch failed:", err));
  }, [isCheckout]);

  useEffect(() => {
    if (!livePricing || options.length === 0) return;
    console.group("[PricingSection] Rendered prices");
    options.forEach((option, i) => {
      const live = livePricing[option.time_period];
      console.log(
        `Option ${i} "${option.label}" (${option.time_period}):`,
        "Storyblok fallback =", option.price + "€",
        "| Stripe live =", live ? (live.amount / 100) + "€" : "N/A",
        "| Strikethrough =", live?.originalPrice ? (live.originalPrice / 100) + "€" : "none",
        "| Badge =", option.badge || "none",
        "| Source =", live ? "Stripe API" : "Storyblok fallback",
      );
    });
    console.groupEnd();
  }, [livePricing, options]);

  /** UTM-Parameter aus der URL, optional ueberschrieben durch die Werte, die
   *  ueber den Google-OAuth-Umweg mitgereist sind. Alle Funnel-Events nutzen
   *  denselben Helper, damit sie sich im Dashboard sauber gruppieren lassen. */
  const readUtms = (overrides?: ResumeData) => {
    if (typeof window === "undefined") return {};
    const params = new URL(window.location.href).searchParams;
    return {
      utm_source: overrides?.utm_source || params.get("utm_source") || undefined,
      utm_campaign: overrides?.utm_campaign || params.get("utm_campaign") || undefined,
      utm_content: overrides?.utm_content || params.get("utm_content") || undefined,
      utm_medium: overrides?.utm_medium || params.get("utm_medium") || undefined,
    };
  };

  const getDisplayPrice = (option: PricingOption): string => {
    if (livePricing) {
      const live = livePricing[option.time_period];
      if (live) return formatEur(live.amount);
    }
    return `${option.price} €`;
  };

  const handleCheckout = async (utmOverrides?: ResumeData) => {
    const selectedOption = options[selectedIndex];
    if (!selectedOption) {
      setError("Bitte wähle einen Plan aus.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const membershipRes = await fetch("/api/membership");
      if (!membershipRes.ok) {
        setError("Mitgliedschaftsstatus konnte nicht geprüft werden. Bitte versuche es erneut.");
        return;
      }
      const membershipData = await membershipRes.json();

      if (membershipData.isActive && membershipData.membership?.status === "active") {
        const portalRes = await fetch("/api/stripe/portal", { method: "POST" });
        const portalData = await portalRes.json();
        if (portalData.url) {
          window.location.href = portalData.url;
        } else {
          setError("Verwaltungsportal konnte nicht geöffnet werden. Bitte versuche es erneut.");
        }
        return;
      }

      const { utm_source: utmSource, utm_campaign: utmCampaign,
              utm_content: utmContent, utm_medium: utmMedium } = readUtms(utmOverrides);

      trackEvent("checkout_started", {
        plan: selectedOption.time_period,
        source: "pricing_section",
        utm_source: utmSource,
        utm_campaign: utmCampaign,
        utm_content: utmContent,
        utm_medium: utmMedium,
      });
      try {
        sessionStorage.setItem("checkout_context", JSON.stringify({
          plan: selectedOption.time_period,
          source: "pricing_section",
          source_path: window.location.pathname,
          utm_source: utmSource,
          utm_campaign: utmCampaign,
          utm_content: utmContent,
          utm_medium: utmMedium,
        }));
      } catch {}

      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan: selectedOption.time_period,
          source: "pricing_section",
          source_path: window.location.pathname,
          cancelPath: window.location.pathname + window.location.search,
          utm_source: utmSource,
          utm_campaign: utmCampaign,
          utm_content: utmContent,
          utm_medium: utmMedium,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || "Checkout konnte nicht gestartet werden. Bitte versuche es erneut.");
      }
    } catch {
      setError("Verbindungsfehler. Bitte prüfe deine Internetverbindung und versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const handleCtaClick = () => {
    if (isCheckout) {
      if (!authLoaded) return;
      // Vor dem Auth-Gate feuern, damit sich "CTA geklickt" von "Stripe
      // erreicht" trennen laesst — checkout_started kommt erst nach Login.
      trackEvent("checkout_cta_clicked", {
        plan: options[selectedIndex]?.time_period,
        is_signed_in: !!isSignedIn,
        source: "pricing_section",
        ...readUtms(),
      });
      if (isSignedIn) {
        handleCheckout();
      } else {
        authCompleted.current = false;
        setAuthModalOpen(true);
      }
    } else if (signupTopic) {
      setModalOpen(true);
    }
  };

  const handleAuthSuccess = () => {
    authCompleted.current = true;
    setAuthModalOpen(false);
    setPendingCheckout(true);
  };

  /** Schliesst der Nutzer das Auth-Modal ohne Erfolg, ist das ein Abbruch an
   *  der Registrierungs-Huerde — der bisher unsichtbare Absprungpunkt. */
  const handleAuthModalOpenChange = (open: boolean) => {
    if (!open && !authCompleted.current) {
      trackEvent("auth_modal_abandoned", {
        plan: options[selectedIndex]?.time_period,
        source: "pricing_section",
        ...readUtms(),
      });
    }
    setAuthModalOpen(open);
  };

  useEffect(() => {
    if (resumeData && authLoaded && isSignedIn && !loading) {
      const utms = resumeData;
      setResumeData(null);
      handleCheckout(utms);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeData, authLoaded, isSignedIn]);

  useEffect(() => {
    if (pendingCheckout && authLoaded && isSignedIn && !loading) {
      setPendingCheckout(false);
      handleCheckout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCheckout, authLoaded, isSignedIn]);

  const buttonHref =
    getUrlFromLinkObject(blok.button_link) || undefined;

  const hasButton = blok.button_label && (buttonHref || signupTopic || (isCheckout && checkoutEnabled));

  return (
    <section id="pricing" className="bg-sky-20 break-container py-8" {...storyblokEditable(blok)}>
      <GridContainer>
        <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3 flex flex-col">
          {/* Headline */}
          {blok.headline && (
            <h2 className="text-headline-xl text-black mb-2">
              {blok.headline}
            </h2>
          )}

          {blok.features && blok.features.length > 0 && (
            <div className="mt-6 md:mt-8 w-full">
              {blok.features.map((nestedBlok: any) => (
                <StoryblokComponent
                  blok={nestedBlok}
                  key={nestedBlok._uid}
                />
              ))}
            </div>
          )}

          {options.length > 0 && (
            <div className="mt-6 md:mt-8 w-full flex flex-col gap-4">
              {options.map((option, index) => (
                <button
                  key={option._uid}
                  type="button"
                  onClick={() => {
                    setSelectedIndex(index);
                    // Nur echte Wechsel zaehlen, nicht ein Re-Klick auf die
                    // ohnehin vorausgewaehlte Kachel.
                    if (index !== selectedIndex) {
                      trackEvent("plan_selected", {
                        plan: option.time_period,
                        source: "pricing_section",
                        ...readUtms(),
                      });
                    }
                  }}
                  className={cn(
                    "relative w-full rounded-[24px] p-6 text-left transition-all",
                    selectedIndex === index
                      ? "bg-white ring-2 ring-sky-100"
                      : "bg-white ring-1 ring-gray-25",
                  )}
                >
                  {option.badge && (
                    <span className="absolute top-6 right-6 text-subline-sm inline-flex items-center rounded-full px-3 py-1 bg-sun-100 text-black">
                      {option.badge}
                    </span>
                  )}

                  <p className="text-copy-bold text-black">{option.label}</p>

                  <div className="flex items-baseline flex-wrap gap-2 mt-1">
                    {option.time_period === "monthly" && (
                      <span className="text-copy text-black-80">nur</span>
                    )}
                    <span className="text-headline-l text-black">
                      {getDisplayPrice(option)}
                    </span>
                    {livePricing?.[option.time_period]?.originalPrice &&
                      livePricing[option.time_period].amount < livePricing[option.time_period].originalPrice! && (
                      <span className="text-copy text-black-80 line-through">
                        {formatEur(livePricing[option.time_period].originalPrice!)}
                      </span>
                    )}
                    <span className="text-copy text-black-80">
                      {periodLabels[option.time_period] || option.time_period}
                    </span>
                  </div>
                  {option.time_period === "yearly" && (() => {
                    const yearly = livePricing?.yearly;
                    const monthlyAmount = yearly
                      ? (yearly.amount / 12 / 100).toFixed(2).replace(".", ",")
                      : (option.price / 12).toFixed(2).replace(".", ",");
                    return (
                      <p className="text-copy-sm text-black-80 mt-1">
                        entspricht {monthlyAmount}{" "}€ pro Monat
                      </p>
                    );
                  })()}
                </button>
              ))}
            </div>
          )}

          {error && (
            <p className="mt-4 text-copy-sm text-red-600">{error}</p>
          )}

          {hasButton && (
            <div className="mt-6 md:mt-8 w-full flex justify-start">
              {isCheckout || signupTopic ? (
                <Button
                  variant="sun"
                  size="lg"
                  onClick={handleCtaClick}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="size-4 animate-spin" /> : <>{blok.button_label}<ArrowRight size="lg" /></>}
                </Button>
              ) : (
                <Button variant="sun" size="lg" asChild>
                  <Link href={buttonHref!}>
                    {blok.button_label}
                    <ArrowRight size="lg" />
                  </Link>
                </Button>
              )}
            </div>
          )}

          {blok.disclaimer_list && blok.disclaimer_list.length > 0 && (
            <div className="mt-6 md:mt-8 w-full flex flex-col gap-2">
              {blok.disclaimer_list.map((nestedBlok: any) => (
                <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
              ))}
            </div>
          )}
        </div>
      </GridContainer>

      {signupTopic && (
        <SignupModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          signupTopic={signupTopic}
          buttonLabel={blok.button_label}
        />
      )}

      {isCheckout && (
        <Dialog open={authModalOpen} onOpenChange={handleAuthModalOpenChange}>
          <DialogContent
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            className="bg-transparent border-none shadow-none max-w-lg p-0"
          >
            <DialogTitle className="sr-only">Anmelden</DialogTitle>
            <AuthCard onSuccess={handleAuthSuccess} skipRedirect source="pricing_section" />
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
};

export default PricingSection;
